import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { conflictingFields, type ConflictFieldPair } from "@/lib/blog/editor-model";
import { LTR_ISOLATE_ATTRS } from "@/lib/blog/locale-options";
import type { BlogLocaleCopy } from "@/lib/blog/types";

interface ConflictDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pairs: ConflictFieldPair[];
  head: BlogLocaleCopy;
  /** Adopt the new base version and replace my values with theirs. */
  onTakeTheirs: () => void;
  /** Adopt the new base version and resubmit my values over theirs. */
  onKeepMine: () => void;
}

/**
 * The stale-version conflict (D17 / M3-S6).
 *
 * Both sides are on screen before either button is offered, and neither path
 * destroys the other's text: "take theirs" replaces the form values but leaves
 * the head intact, "keep mine" adopts the new base version and resubmits — the
 * previous head is still the previous revision, and history is append-only. The
 * client never re-merges; the editor chooses.
 */
export function ConflictDialog({
  open,
  onOpenChange,
  pairs,
  head,
  onTakeTheirs,
  onKeepMine,
}: ConflictDialogProps) {
  const changed = conflictingFields(pairs);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="conflict-dialog">
        <DialogHeader>
          <DialogTitle>Someone else saved this copy first</DialogTitle>
          <DialogDescription>
            The {head.locale} copy is now at v{head.version}. Your edits are still here — nothing
            has been discarded. Choose which version survives, field by field is not offered: the
            whole copy moves one way or the other, and the other stays in the history.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-96 overflow-y-auto">
          {changed.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="conflict-no-differences">
              Nothing you typed differs from what they saved. Adopting their version loses nothing.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th scope="col" className="p-2">
                    Field
                  </th>
                  <th scope="col" className="p-2">
                    Mine
                  </th>
                  <th scope="col" className="p-2">
                    Theirs (v{head.version})
                  </th>
                </tr>
              </thead>
              <tbody>
                {changed.map((pair) => (
                  <tr key={pair.path} className="border-b align-top">
                    <th scope="row" className="p-2 text-left font-normal">
                      <span className="block">{pair.label}</span>
                      <span
                        className="block font-mono text-xs text-muted-foreground"
                        {...LTR_ISOLATE_ATTRS}
                      >
                        {pair.path}
                      </span>
                    </th>
                    <td className="p-2">{pair.mine}</td>
                    <td className="p-2">{pair.theirs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onTakeTheirs}>
            Take theirs
          </Button>
          <Button onClick={onKeepMine}>Keep mine, save over v{head.version}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
